import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Row, Col, Modal } from "react-bootstrap";
import { OrderItem } from "../../../components/OrderItem";
import { hrStyle } from "../../../components/ShoppingCart";
import { formatCurrency, formatSignedCurrency } from "../../../utilities/formatCurrency";
import { getOrderCostLines, normalizeOrderCost } from "../../../lib/orderCost";
import { fetchAllOrders } from "../../../lib/webService/webService";
import { useAuthCheck } from "../../../utilities/authCheck";
import { useUserContext } from "../../../context/UserContext";
import { PullToRefreshWrapper } from "../../../components/pullRefresh";
import {
  Receipt,
  Person,
  Telephone,
  CashStack,
  Funnel,
  XLg,
  ExclamationTriangle,
  ShieldExclamation,
  Hourglass,
  CheckCircle,
  Gear,
  Truck,
  BoxSeam,
} from "react-bootstrap-icons";
import { Seo } from "../../../components/Seo";
import { generateNoIndexSEO } from "../../../lib/seo/metaGenerators";

// ─── Module-level CSS ────────────────────────────────────────────────────────
if (typeof document !== "undefined" && !document.getElementById("os-styles")) {
  const s = document.createElement("style");
  s.id = "os-styles";
  s.textContent = `
    @keyframes _os_sk { 0%,100%{opacity:1}50%{opacity:.4} }
    ._os_sk { animation: _os_sk 1.8s ease-in-out infinite; background: rgba(60,60,67,0.08); }
    .os-card { transition: transform 0.22s cubic-bezier(0.34,1.24,0.64,1), box-shadow 0.22s ease; }
    .os-card:hover { transform: translateY(-3px); box-shadow: 0 8px 24px rgba(0,0,0,0.09) !important; }
    .os-modal .modal-content { border-radius: 20px !important; border: none !important; overflow: hidden; }
    .os-info-grid { display:grid; grid-template-columns:1fr 1fr; gap:16px; background:rgba(118,118,128,.07); border-radius:12px; padding:16px; }
  `;
  document.head.appendChild(s);
}

const FONT =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif';

const STATUS_CONFIG = {
  placed:     { color: "#8E8E93", icon: <Hourglass size={11} /> },
  accepted:   { color: "#007AFF", icon: <CheckCircle size={11} /> },
  processing: { color: "#FF9500", icon: <Gear size={11} /> },
  enroute:    { color: "#5856D6", icon: <Truck size={11} /> },
  delivered:  { color: "#34C759", icon: <CheckCircle size={11} /> },
};

const SORT_OPTIONS = [
  { value: "cost", label: "Cost amount" },
  { value: "date", label: "Date" },
];

const FILTER_OPTIONS = [
  { value: "placed", label: "Placed" },
  { value: "accepted", label: "Accepted" },
  { value: "processing", label: "Processing" },
  { value: "enroute", label: "Enroute" },
  { value: "delivered", label: "Delivered" },
  { value: "paid", label: "Paid" },
];

const Skel = ({ w, h, r = 8, style = {} }) => (
  <div className="_os_sk" style={{ width: w, height: h, borderRadius: r, ...style }} />
);

const StatusPill = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? { color: "#8E8E93", icon: null };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "3px 10px 3px 8px",
        borderRadius: 20,
        backgroundColor: `${cfg.color}1A`,
        color: cfg.color,
        fontSize: "0.72rem",
        fontWeight: 600,
        textTransform: "capitalize",
        letterSpacing: "0.01em",
      }}
    >
      {cfg.icon}
      {status}
    </span>
  );
};

function OrderDetailsModal({ show, onHide, orderId, cost, cartItems, status }) {
  const total = normalizeOrderCost(cost).total;

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName="os-modal">
      <Modal.Body style={{ padding: "24px", fontFamily: FONT }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", margin: "0 0 4px", letterSpacing: "-0.02em" }}>
              Order Details
            </h3>
            <span style={{ fontSize: "0.78rem", color: "rgba(60,60,67,0.42)", fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
              #{orderId}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <StatusPill status={status} />
            <button
              onClick={onHide}
              style={{
                width: 28, height: 28, borderRadius: "50%",
                background: "rgba(118,118,128,.12)", border: "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", color: "rgba(60,60,67,0.55)", flexShrink: 0,
              }}
            >
              <XLg size={12} />
            </button>
          </div>
        </div>

        <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
          Order Items
        </div>
        <div style={{ marginBottom: 20 }}>
          {cartItems.map((item) => (
            <div key={item.id} className="mb-3">
              <OrderItem key={item.id} {...item} />
              <hr style={hrStyle} />
            </div>
          ))}
        </div>

        <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
          Order Summary
        </div>
        <div className="os-info-grid" style={{ gridTemplateColumns: "1fr", gap: 10 }}>
          {getOrderCostLines(cost).map(({ label, value }) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.52)" }}>{label}</span>
              <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#1c1c1e" }}>{formatSignedCurrency(value)}</span>
            </div>
          ))}
          <div style={{ borderTop: "1px solid rgba(60,60,67,0.1)", paddingTop: 10, marginTop: 2, display: "flex", justifyContent: "space-between" }}>
            <span style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#1c1c1e" }}>Total</span>
            <span style={{ fontSize: "1rem", fontWeight: 700, color: "#007AFF" }}>{formatCurrency(total)}</span>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

const OrderCard = ({ order, onViewDetails }) => (
  <div
    className="os-card"
    style={{
      backgroundColor: "#fff",
      borderRadius: 16,
      border: "1px solid rgba(60,60,67,0.09)",
      padding: "14px 16px",
      height: "100%",
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      fontFamily: FONT,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div
          style={{
            width: 32, height: 32, borderRadius: 9,
            background: "rgba(0,122,255,0.10)",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}
        >
          <Receipt size={14} color="#007AFF" />
        </div>
        <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#1c1c1e", fontVariantNumeric: "tabular-nums" }}>
          #{order?.id}
        </span>
      </div>
      <StatusPill status={order.status} />
    </div>

    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
      <Person size={12} color="rgba(60,60,67,0.4)" />
      <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#1c1c1e" }}>
        {order?.firstName} {order?.lastName}
      </span>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
      <Telephone size={12} color="rgba(60,60,67,0.4)" />
      <span style={{ fontSize: "0.8125rem", color: "rgba(60,60,67,0.55)" }}>{order?.phone}</span>
    </div>

    <div
      style={{
        marginTop: "auto",
        paddingTop: 10,
        borderTop: "1px solid rgba(60,60,67,0.07)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <CashStack size={13} color="#34C759" />
        <span style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.02em" }}>
          {formatCurrency(order?.cost.subTotal)}
        </span>
      </div>
      <button
        onClick={() => onViewDetails(order)}
        style={{
          background: "none", border: "none", padding: 0,
          fontSize: "0.8125rem", fontWeight: 600, color: "#007AFF", cursor: "pointer",
        }}
      >
        View →
      </button>
    </div>
  </div>
);

const SkeletonGrid = () => (
  <Row className="g-3">
    {Array.from({ length: 6 }).map((_, i) => (
      <Col xs={12} sm={6} lg={4} key={i}>
        <div style={{ backgroundColor: "#fff", borderRadius: 16, border: "1px solid rgba(60,60,67,0.08)", padding: "14px 16px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <Skel w={90} h={22} r={9} />
            <Skel w={70} h={20} r={20} />
          </div>
          <Skel w="60%" h={14} r={5} style={{ marginBottom: 8 }} />
          <Skel w="45%" h={13} r={5} style={{ marginBottom: 16 }} />
          <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 10 }}>
            <Skel w={70} h={16} r={5} />
            <Skel w={40} h={13} r={5} />
          </div>
        </div>
      </Col>
    ))}
  </Row>
);

const EmptyState = () => (
  <div style={{ textAlign: "center", padding: "64px 24px", background: "#fff", border: "1px solid rgba(60,60,67,0.08)", borderRadius: 16 }}>
    <div style={{ width: 56, height: 56, borderRadius: 16, background: "rgba(60,60,67,0.06)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
      <BoxSeam size={26} color="rgba(60,60,67,0.28)" />
    </div>
    <h4 style={{ fontSize: "1rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 6 }}>No Orders Found</h4>
    <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.45)", margin: 0 }}>
      There are no orders available at the moment.
    </p>
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div style={{ textAlign: "center", padding: "48px 24px", background: "#fff", border: "1px solid rgba(60,60,67,0.08)", borderRadius: 16 }}>
    <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,59,48,.10)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
      <ExclamationTriangle size={26} color="#FF3B30" />
    </div>
    <h4 style={{ fontSize: "1rem", fontWeight: 600, color: "#1c1c1e", marginBottom: 6 }}>Error Loading Orders</h4>
    <p style={{ fontSize: "0.875rem", color: "rgba(60,60,67,0.55)", marginBottom: 20 }}>{error}</p>
    <button
      onClick={onRetry}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 22px",
        background: "#007AFF", color: "#fff", border: "none", borderRadius: 12,
        fontSize: "0.9rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT,
      }}
    >
      Try Again
    </button>
  </div>
);

const OrdersSummary = () => {
  const { isAuthorized, isLoading: isAuthLoading } = useAuthCheck();
  const { token } = useUserContext();
  const [orders, setOrders] = useState([]);
  const [isOrdersLoading, setIsOrdersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [sortList, setSortList] = useState([]);
  const [filterList, setFilterList] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const handleClose = () => setShowFilterModal(false);
  const handleShow = () => setShowFilterModal(true);

  const closeOrderDetails = () => {
    setSelectedOrder(null);
    if (searchParams.has("orderId")) {
      searchParams.delete("orderId");
      setSearchParams(searchParams, { replace: true });
    }
  };

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const loadOrders = useCallback(async () => {
    if (!isAuthorized || !token) return;

    try {
      setIsOrdersLoading(true);
      const data = await fetchAllOrders(token);

      if (!isMountedRef.current) return;

      if (Array.isArray(data)) {
        setOrders(data);
      } else {
        console.error("Received non-array data from API:", data);
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      if (isMountedRef.current) {
        setError(error.message);
        setOrders([]);
      }
    } finally {
      if (isMountedRef.current) {
        setIsOrdersLoading(false);
      }
    }
  }, [isAuthorized, token]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // Deep-link support: a notification click navigates here with ?orderId=,
  // so once orders are loaded, auto-open that order's existing details modal.
  useEffect(() => {
    const orderId = searchParams.get("orderId");
    if (!orderId || orders.length === 0) return;
    const match = orders.find((o) => String(o?.id) === orderId);
    if (match) setSelectedOrder(match);
  }, [searchParams, orders]);

  const onSortChange = (e) => {
    const value = e.target.value;
    const itemExists = sortList.includes(value);
    var arr = sortList;
    if (itemExists) {
      var index = arr.indexOf(value);
      if (index >= 0) {
        arr.splice(index, 1);
      }
    } else {
      arr.push(value);
    }
    setSortList(arr);
  };

  const onFilterChange = (e) => {
    const value = e.target.value;
    const itemExists = filterList.includes(value);
    var arr = filterList;
    if (itemExists) {
      var index = arr.indexOf(value);
      if (index >= 0) {
        arr.splice(index, 1);
      }
    } else {
      arr.push(value);
    }
    setFilterList(arr);
  };

  const clearFilters = () => {
    handleClose();
  };

  if (isAuthLoading || !isAuthorized) {
    return (
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 16px" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
          {!isAuthLoading && !isAuthorized && (
            <>
              <div style={{ width: 60, height: 60, borderRadius: 18, backgroundColor: "rgba(255,149,0,.12)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                <ShieldExclamation size={28} color="#FF9500" />
              </div>
              <h3 style={{ fontSize: "1.125rem", fontWeight: 700, color: "#1c1c1e", marginBottom: 6, textAlign: "center" }}>
                Redirecting…
              </h3>
            </>
          )}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 16px" }}>
        <ErrorState error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <PullToRefreshWrapper onRefresh={loadOrders}>
      <Seo {...generateNoIndexSEO("Orders Summary")} />
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 16px 48px", fontFamily: FONT }}>

        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
          <h2 style={{ fontSize: "1.375rem", fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.03em", margin: 0 }}>
            Orders
          </h2>
          <button
            onClick={handleShow}
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "9px 16px", borderRadius: 20,
              border: "1px solid rgba(0,122,255,0.35)", backgroundColor: "transparent",
              color: "#007AFF", fontSize: "0.8125rem", fontWeight: 600,
              cursor: "pointer", fontFamily: FONT,
            }}
          >
            <Funnel size={13} /> Filter Orders
          </button>
        </div>

        {/* Content */}
        {isOrdersLoading ? (
          <SkeletonGrid />
        ) : !orders || orders.length === 0 ? (
          <EmptyState />
        ) : (
          <Row className="g-3">
            {orders.map((order) => (
              <Col xs={12} sm={6} lg={4} key={order?.id}>
                <OrderCard order={order} onViewDetails={setSelectedOrder} />
              </Col>
            ))}
          </Row>
        )}

        {/* Order details modal */}
        {selectedOrder && (
          <OrderDetailsModal
            show={!!selectedOrder}
            onHide={closeOrderDetails}
            orderId={selectedOrder.id}
            cost={selectedOrder.cost}
            cartItems={selectedOrder.cart}
            status={selectedOrder.status}
          />
        )}

        {/* Filter & sort modal */}
        <Modal show={showFilterModal} onHide={handleClose} backdrop="static" centered dialogClassName="os-modal">
          <Modal.Body style={{ padding: "24px", fontFamily: FONT }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
              <h3 style={{ fontSize: "1.0625rem", fontWeight: 700, color: "#1c1c1e", margin: 0, letterSpacing: "-0.02em" }}>
                Filter & Sort Orders
              </h3>
              <button
                onClick={handleClose}
                style={{
                  width: 28, height: 28, borderRadius: "50%",
                  background: "rgba(118,118,128,.12)", border: "none",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", color: "rgba(60,60,67,0.55)", flexShrink: 0,
                }}
              >
                <XLg size={12} />
              </button>
            </div>

            <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
              Sort by
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
              {SORT_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "9px 14px", borderRadius: 12,
                    border: "1px solid rgba(60,60,67,0.14)",
                    fontSize: "0.8125rem", color: "#1c1c1e", cursor: "pointer",
                  }}
                >
                  <input type="radio" name="sort" value={opt.value} onChange={onSortChange} />
                  {opt.label}
                </label>
              ))}
            </div>

            <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "rgba(60,60,67,0.55)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
              Filter by Status
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 24 }}>
              {FILTER_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    padding: "9px 14px", borderRadius: 12,
                    border: "1px solid rgba(60,60,67,0.14)",
                    fontSize: "0.8125rem", color: "#1c1c1e", cursor: "pointer",
                  }}
                >
                  <input type="checkbox" value={opt.value} onChange={onFilterChange} />
                  {opt.label}
                </label>
              ))}
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={clearFilters}
                style={{
                  flex: 1, padding: "11px", borderRadius: 12,
                  border: "1px solid rgba(60,60,67,0.14)", background: "transparent",
                  fontSize: "0.9rem", fontWeight: 600, color: "#1c1c1e", cursor: "pointer", fontFamily: FONT,
                }}
              >
                Clear All
              </button>
              <button
                onClick={handleClose}
                style={{
                  flex: 1, padding: "11px", borderRadius: 12, border: "none",
                  backgroundColor: "#007AFF", color: "#fff",
                  fontSize: "0.9rem", fontWeight: 600, cursor: "pointer", fontFamily: FONT,
                }}
              >
                Apply Filters
              </button>
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </PullToRefreshWrapper>
  );
};

export default OrdersSummary;
